import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */
import useStyles from 'features/setting/personnel-managements/roles/style'

/** API */
import RolesApi from 'api/setting/default-users/role.api'
import { routeName } from 'routes/routes-name'

import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment } from '@mui/material'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { notiError } from 'component/notifications/notifications'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import MainButton from 'new-components/buttons/main-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}

export default function Roles() {
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()

  const [roles, setRoles] = useState([])

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<any>("success")
  const [labelSnack, setLabelSnack] = useState('')


  const loadData = async () => {
    try {
      const resp = await RolesApi.findAll({
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status
      })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setRoles(resp.data)
      } else {
        setRoles([])
      }
    } catch (error) {
      notiError('เกิดข้อผิดพลาด')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])

  const onCreate = () => {
    history.push(routeName.roles + '/create')
  }

  const onActive = () => {
    if (id) {
      RolesApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('ROLE.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('ROLE.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onDelete = () => {
    if (id) {
      RolesApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('ROLE.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('ROLE.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (objData: any) => {
    history.push(routeName.roles + '/update', objData)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }
  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }
  const headCells = [
    { id: 'no', disablePadding: false, label: t('TABLE.NO') },
    { id: 'roleName', disablePadding: false, label: t('ROLE.TABLE.CELL.ROLE_NAME') },
    { id: 'branchName', disablePadding: false, label: t('ROLE.TABLE.CELL.BRANCH_USE_ROLE') },
    { id: 'status', disablePadding: false, label: t('ROLE.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('ROLE.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { roleId, roleName, updatedBy, updatedAt, status: rolesStatus, roleBranches } = objData
    const branches: any = roleBranches?.map(({ branches: branchesValue }: any) => branchesValue?.branchName).filter((value: any, index: any, self: any) => self.indexOf(value) === index)
    const statusBtnActive = rolesStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = rolesStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: roleId,
      id: roleId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: roleName },
        { option: 'TEXT', align: 'left', label: branches.join(', ') },
        { option: 'STATUS', align: 'center', label: rolesStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.roleName)
  }

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`ROLE.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`ROLE.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { roleName: name, status: t(`ROLE.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('ROLE.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <TextInput
              label={t('ROLE.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} xl={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={8}>
            <Box display={'flex'} justifyContent={'space-between'}>
              <MainButton
                title={t('INPUT.SEARCH')}
                type='primary'
                variant='contained'
                style={{ width: '120px' }}
                onClick={() => {
                  setPage(1)
                  loadData()
                }} />
              <MainButton
                title={t('ROLE.BUTTON.ADD')}
                disabled={permissions.isCreate.disabled}
                type='primary'
                variant='contained'
                onClick={() => onCreate()}
                startIcon={<AddCircleIcon />}
              />
            </Box>
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={roles.map((val, i) => {
              return renderData(val, i)
            })}
          />
        </Box>
      </Box>
    </Box>
  )
}
